import type React from "react";
import { Chip, type ChipProps } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import { OrderStatus } from "../../types/order";

type TranslationKeys = keyof typeof translations.en;

const ORDER_STATUS_COLORS: Record<OrderStatus, ChipProps["color"]> = {
  [OrderStatus.OPEN]: "default",
  [OrderStatus.READY_FOR_REVIEW]: "default",
  [OrderStatus.IN_REVIEW]: "warning",
  [OrderStatus.REVISION_REQUIRED]: "error",
  [OrderStatus.VERIFIED]: "info",
  [OrderStatus.APPROVED]: "info",
  [OrderStatus.APPROVED_WITH_OVERRIDE]: "success",
  [OrderStatus.COMPLETED]: "success",
};

const EXTRA_STATUS_COLORS: Record<string, ChipProps["color"]> = {
  invoicing_review: "info",
  surat_jalan_review: "info",
};

/** Shared by order/report status chips and change-history timeline nodes. */
export const getEntityStatusChipColor = (status: OrderStatus | string): ChipProps["color"] =>
  ORDER_STATUS_COLORS[status as OrderStatus] ?? EXTRA_STATUS_COLORS[status] ?? "default";

/** Resolve status chip color to a concrete theme palette value. */
export const getEntityStatusPaletteColor = (
  status: OrderStatus | string,
  theme: { palette: Theme["palette"] },
): string => {
  const chipColor = getEntityStatusChipColor(status);
  if (!chipColor || chipColor === "default") {
    return theme.palette.grey[500];
  }
  return theme.palette[chipColor].main;
};

interface OrderStatusChipProps {
  status: OrderStatus | string;
  "data-testid"?: string;
}

const OrderStatusChip: React.FC<OrderStatusChipProps> = ({ status, "data-testid": testId }) => {
  const { t } = useTranslation();
  const color = getEntityStatusChipColor(status);

  return (
    <Chip
      label={t(status as TranslationKeys)}
      size="small"
      color={color}
      variant="filled"
      data-testid={testId}
      sx={{
        fontWeight: 500,
        borderRadius: 1,
        height: 24,
        "& .MuiChip-label": {
          px: 1,
          fontSize: "0.75rem",
        },
      }}
    />
  );
};

export default OrderStatusChip;
